import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import Button from 'react-bootstrap/Button';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'; // TODO try to add this to date.js review comments
import { format } from 'date-fns';
import { Api } from './interface';
import { toast } from 'react-toastify';
import {
  ROWSPERPAGE,
  CAPITAL_PURCHASE_PRIORITY_COLUMNS,
  CAPITAL_PURCHASE_COLUMN_LABELS,
  CAPITAL_PURCHASE_EXCLUDED_COLUMNS,
  CAPITAL_PURCHASE_RIGHT_ALIGN_COLUMNS,
  CAPITAL_PURCHASE_DATE_COLUMNS,
} from './constants';

export default function CapitalPurchaseReport() {
  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [error, setError] = useState(null);

  // PO Number related state
  const [poNumbers, setPONumbers] = useState([]);
  const [selectedPONumbers, setSelectedPONumbers] = useState([]);
  const [poLoading, setPOLoading] = useState(false);
  const [poInputValue, setPOInputValue] = useState('');

  // Fetch PO Numbers on component mount
  useEffect(() => {
    fetchPONumbers();
  }, []);

  // Function to fetch PO numbers
  const fetchPONumbers = async () => {
    setPOLoading(true);
    try {
      const response = await Api({
        sp: 'getPurchaseOrders',
        json: {},
      });

      // Transform response to array of PO numbers if needed
      const poList = Array.isArray(response)
        ? response.map((item) => item.PONumber || item['PO Number'] || item)
        : [];

      setPONumbers(poList);
    } catch (error) {
      console.error('Error fetching PO numbers:', error);
      toast.error('Failed to fetch PO numbers');
      setPONumbers([]);
    } finally {
      setPOLoading(false);
    }
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    // Check if we have a custom label mapping
    if (CAPITAL_PURCHASE_COLUMN_LABELS[key]) {
      return CAPITAL_PURCHASE_COLUMN_LABELS[key];
    }

    // Default: use the column name as-is from the stored procedure
    return key;
  };

  // Helper function to determine column alignment
  const getColumnAlignment = (key) => {
    return CAPITAL_PURCHASE_RIGHT_ALIGN_COLUMNS.includes(key)
      ? 'right'
      : 'left';
  };

  // Function to generate columns from SP response
  const generateColumns = (data) => {
    if (!data || data.length === 0) return [];

    const firstRow = data[0];
    const allColumnKeys = Object.keys(firstRow);

    // Filter out columns that should not be displayed
    const filteredColumnKeys = allColumnKeys.filter(
      (key) => !CAPITAL_PURCHASE_EXCLUDED_COLUMNS.includes(key)
    );

    // Separate priority columns from others
    const priorityCols = [];
    const otherCols = [];

    filteredColumnKeys.forEach((key) => {
      const columnConfig = {
        id: key,
        label: formatColumnLabel(key),
        align: getColumnAlignment(key),
      };

      if (CAPITAL_PURCHASE_PRIORITY_COLUMNS.includes(key)) {
        priorityCols.push(columnConfig);
      } else {
        otherCols.push(columnConfig);
      }
    });

    // Sort priority columns according to CAPITAL_PURCHASE_PRIORITY_COLUMNS order
    priorityCols.sort((a, b) => {
      const aIndex = CAPITAL_PURCHASE_PRIORITY_COLUMNS.indexOf(a.id);
      const bIndex = CAPITAL_PURCHASE_PRIORITY_COLUMNS.indexOf(b.id);
      return aIndex - bIndex;
    });

    // Return priority columns first, then others
    return [...priorityCols, ...otherCols];
  };

  const fetchReportData = async () => {
    // Validate inputs - either date range OR PO numbers must be provided
    if ((!startDate || !endDate) && selectedPONumbers.length === 0) {
      setError('Please select either a date range or PO numbers');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData = {};

      // Add date range if provided
      if (startDate && endDate) {
        requestData.startDate = format(startDate, 'yyyy-MM-dd');
        requestData.endDate = format(endDate, 'yyyy-MM-dd');
      }

      // Add PO numbers if provided
      if (selectedPONumbers.length > 0) {
        const poNumbersArray = selectedPONumbers.map(
          (poObject) => poObject.po_num
        );
        requestData.poNumbers = poNumbersArray;
      }

      const response = await Api({
        sp: 'getAssetPurchaseInfo',
        json: requestData,
      });

      const data = response || [];
      setReportData(data);
      setTotalRows(data.length);
      setPage(0);

      // Generate columns from response data
      const generatedColumns = generateColumns(data);
      setColumns(generatedColumns);

      if (data.length === 0) {
        toast.info('No data found for the selected criteria');
      } else {
        toast.success(`Found ${data.length} records`);
      }
    } catch (error) {
      console.error('Error fetching capital purchase report:', error);
      setError('Failed to fetch report data. Please try again.');
      toast.error('Failed to fetch report data');
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatCellValue = (value, columnId) => {
    // Format specific columns based on their ID
    if (
      CAPITAL_PURCHASE_DATE_COLUMNS.includes(columnId) ||
      columnId.toLowerCase().includes('date')
    ) {
      return formatDate(value);
    }

    return value || '';
  };

  const exportToCSV = () => {
    if (!reportData.length) {
      toast.warning('No data to export');
      return;
    }

    const headers = columns.map((col) => col.label).join(',');
    const csvData = reportData
      .map((row) =>
        columns
          .map((col) => {
            let value = formatCellValue(row[col.id], col.id);
            // Escape quotes and wrap in quotes for CSV
            return `"${String(value).replace(/"/g, '""')}"`;
          })
          .join(',')
      )
      .join('\n');

    const csvContent = `${headers}\n${csvData}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `Capital_Purchase_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    }
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedPONumbers([]);
    setPOInputValue('');
    setReportData([]);
    setColumns([]);
    setTotalRows(0);
    setPage(0);
    setError(null);
  };

  // Check if generate button should be enabled
  const canGenerateReport = () => {
    return (startDate && endDate) || selectedPONumbers.length > 0;
  };

  // Add these helper functions at the component level
  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    if (newValue && selectedPONumbers.length > 0) {
      setSelectedPONumbers([]);
      setPOInputValue('');
      toast.info('PO Numbers cleared - using date range filter');
    }
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
    if (newValue && selectedPONumbers.length > 0) {
      setSelectedPONumbers([]);
      setPOInputValue('');
      toast.info('PO Numbers cleared - using date range filter');
    }
  };

  const handlePONumbersChange = (event, newValue) => {
    setSelectedPONumbers(newValue);

    if (newValue.length > 0 && (startDate || endDate)) {
      setStartDate(null);
      setEndDate(null);
      toast.info('Date range cleared - using PO numbers filter');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="main-container">
        <div className="mt-2 d-flex justify-content-between">
          {/* Page Title */}
          <h2>Capital Purchase Report</h2>
        </div>

        {/* Filters Section */}
        <Paper className="capital-purchase-filters-paper">
          <div className="pb-2">
            <p className="mb-3 text-muted">
              Select either a date range of Purchase Order creation or specific
              Purchase Order Numbers to generate the report.
            </p>

            {/* Date Range Section */}
            <div className="mb-4">
              <h6 className="fw-semibold mb-2">
                Date Range{' '}
                {selectedPONumbers.length > 0 && (
                  <span className="text-muted fw-normal small">
                    (cleared when PO numbers selected)
                  </span>
                )}
              </h6>
              <div className="row g-2 mb-3">
                <div className="col-md-5 capital-purchase-date-range">
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                    maxDate={endDate || new Date()}
                  />
                </div>
                <div className="col-md-5 capital-purchase-date-range">
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                    minDate={startDate}
                    maxDate={new Date()}
                  />
                </div>
              </div>
            </div>

            {/* PO Numbers Section */}
            <div className="mb-4">
              <h6 className="fw-semibold mb-2">
                PO Numbers{' '}
                {(startDate || endDate) && (
                  <span className="text-muted fw-normal small">
                    (cleared when date range selected)
                  </span>
                )}
              </h6>
              <div className="row g-2 mb-3">
                <div className="col-md-10">
                  <Autocomplete
                    multiple
                    value={selectedPONumbers}
                    onChange={handlePONumbersChange}
                    inputValue={poInputValue}
                    onInputChange={(event, newInputValue) => {
                      setPOInputValue(newInputValue);
                    }}
                    options={poNumbers}
                    getOptionLabel={(option) =>
                      option ? `${option.po_num}` : ''
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.pi_dbid}>
                        <div>
                          <div className="fw-bold">{option.po_num}</div>
                          <div className="add-entity-option-io-po">
                            {option.pi_description}
                          </div>
                        </div>
                      </li>
                    )}
                    loading={poLoading}
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select PO numbers"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {poLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const searchTerm = inputValue.toLowerCase();

                      return options.filter((option) => {
                        const poNum = String(option.po_num || '').toLowerCase();
                        const poDesc = String(
                          option.pi_description || ''
                        ).toLowerCase();

                        return (
                          poNum.includes(searchTerm) ||
                          poDesc.includes(searchTerm)
                        );
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2">
              <Button
                onClick={fetchReportData}
                disabled={loading || !canGenerateReport()}
                variant="primary"
              >
                {loading ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Generating...
                  </>
                ) : (
                  'Generate Report'
                )}
              </Button>
              <Button
                variant="outline-primary"
                onClick={exportToCSV}
                disabled={!reportData.length || loading}
                className="btn-action"
              >
                Export CSV
              </Button>
              <Button
                variant="outline-secondary"
                onClick={clearFilters}
                disabled={loading}
                className="btn-action"
              >
                Clear All
              </Button>
            </div>
          </div>
        </Paper>

        {/* Error Alert */}
        {error && (
          <div
            className="alert alert-danger alert-dismissible mb-3"
            role="alert"
          >
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Data Table */}
        <Paper className="table-container">
          <TableContainer className="tableContainer">
            <Table
              stickyHeader
              aria-label="capital purchase report table"
              size="small"
              className="capital-purchase-table"
            >
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                      className="capital-purhase-head-cell fw-bold"
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.Entity_DBID || index}
                      className="capital-purchase-table-row"
                    >
                      {columns.map((column) => {
                        const value = formatCellValue(
                          row[column.id],
                          column.id
                        );
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align || 'left'}
                            className="capital-purchase-table-cell"
                            title={String(value)} // Tooltip for long text
                          >
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {reportData.length > 0 && (
            <TablePagination
              labelRowsPerPage="Rows:"
              rowsPerPageOptions={ROWSPERPAGE}
              component="div"
              className="capital-table-pagination"
              count={totalRows}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>

        {/* No Data Message */}
        {!loading &&
          reportData.length === 0 &&
          ((startDate && endDate) || selectedPONumbers.length > 0) &&
          !error && (
            <Paper className="no-data-container">
              <div className="text-center py-5">
                <h5 className="text-muted mb-2">
                  No capital purchase data found
                </h5>
                <p className="text-muted">
                  No assets were found for the selected criteria
                </p>
              </div>
            </Paper>
          )}

        {/* Initial State Message */}
        {!loading &&
          reportData.length === 0 &&
          !startDate &&
          !endDate &&
          selectedPONumbers.length === 0 && (
            <Paper className="no-data-container">
              <div className="text-center py-5">
                <h5 className="text-muted mb-2">
                  Select criteria to generate the report
                </h5>
                <p className="text-muted">
                  Choose a PO create date range or specific PO numbers above to
                  view capital purchase data{' '}
                </p>
              </div>
            </Paper>
          )}
      </div>
    </LocalizationProvider>
  );
}
